import * as React from 'react';
import { alpha, Box, Button, Checkbox, CircularProgress, FormControlLabel, IconButton, Stack, Tooltip } from '@mui/material';
import { theme } from '../../../../../../../common/styles/theme';
import { AudioRest, IRestProviderCaptions, TCaptionProvider } from '../../../../../../../common/rest/audio/audioRest';
import { TrashIcon } from '../../../../../../../../icons/TrashIcon';
import { CheckIcon } from '../../../../../../../../icons/CheckIcon';
import { ConferenceRest, TConferenceCompareItem } from '../../../../../../../common/rest/conference/conferenceRest';
import { mergeProgressLog } from '../../../../../../../common/utils/conferenceProgress';
import { AudioPlayerManager } from '../../../../../../effects/audioPlayer/audioPlayerManager';
import { CallRecordingProgressModalHOC } from '../../../../../call/CallRecordingProgressModal/hocs/CallRecordingProgressModalHOC';
import { buildAlignedRows, compareProgressMessage } from '../utils';
import { CONFERENCE_PROVIDERS } from '../../../../../../../common/constants';
import { ICaptionsCompareProps } from '../types';

const formatTime = (seconds: number): string => {
	const total = Math.max(0, Math.floor(seconds));
	const mm = Math.floor(total / 60).toString().padStart(2, '0');
	const ss = (total % 60).toString().padStart(2, '0');
	return `${mm}:${ss}`;
};

const providerLabel = (provider: string | null): string => provider || 'Legacy';

export function CaptionsCompare({
	audioId,
	roomName,
	confProvider,
	onProviderChange,
}: ICaptionsCompareProps) {
	const [groups, setGroups] = React.useState<IRestProviderCaptions[]>([]);
	const [results, setResults] = React.useState<TConferenceCompareItem[]>([]);
	const [selected, setSelected] = React.useState<Record<string, boolean>>({});
	// Existing sets aren't known until groups load, so the default selection is applied once per audio after the first load.
	const selectionInitedRef = React.useRef(false);
	const [loading, setLoading] = React.useState(false);
	const [running, setRunning] = React.useState(false);
	const [deleting, setDeleting] = React.useState(false);
	const [logOpen, setLogOpen] = React.useState(false);

	// Compare runs async on the backend; poll its endpoint and drive the modal from this local state
	// (its progress shape differs from the conference pipeline's, so it bypasses the audio store).
	const [progressLog, setProgressLog] = React.useState<string[]>([]);
	const [startedAt, setStartedAt] = React.useState<number | undefined>(undefined);
	const [stepStartedAt, setStepStartedAt] = React.useState<number | undefined>(undefined);
	const [updatedAt, setUpdatedAt] = React.useState<number | undefined>(undefined);
	const pollRef = React.useRef<number | null>(null);
	const lastLineRef = React.useRef<string>('');
	// Guards the post-await setInterval: if we unmount while compare() is in flight, the unmount
	// cleanup has already run and can't clear an interval that doesn't exist yet.
	const aliveRef = React.useRef(true);

	const stopPoll = React.useCallback(() => {
		if (pollRef.current !== null) {
			clearInterval(pollRef.current);
			pollRef.current = null;
		}
	}, []);
	React.useEffect(() => {
		aliveRef.current = true; // re-arm on (re)mount so StrictMode's mount→cleanup→remount doesn't leave us dead
	}, []);
	React.useEffect(() => () => {
		aliveRef.current = false;
		stopPoll();
	}, [stopPoll]); // stop on unmount

	// Merge a line into the log; restart the step timer when the line changes.
	const pushLog = React.useCallback((message: string) => {
		if (message !== lastLineRef.current) {
			lastLineRef.current = message;
			setStepStartedAt(Date.now());
		}
		setProgressLog(prev => mergeProgressLog(prev, message));
	}, []);

	const loadGroups = React.useCallback(async (resetSelection = false) => {
		setLoading(true);
		try {
			const all = await AudioRest.getCaptions(audioId, 'all');
			const list = all || [];
			setGroups(list);
			// Default to running only the providers that have no captions yet — on first load and after each run.
			if (resetSelection || !selectionInitedRef.current) {
				selectionInitedRef.current = true;
				const existing = new Set(list.map(g => g.provider));
				setSelected(Object.fromEntries(CONFERENCE_PROVIDERS.map(p => [p, !existing.has(p)])));
			}
		} finally {
			setLoading(false);
		}
	}, [audioId]);

	React.useEffect(() => {
		selectionInitedRef.current = false; // re-arm the default selection when the audio changes
		loadGroups();
	}, [loadGroups]);

	const toggleProvider = (provider: string) => {
		setSelected(prev => ({ ...prev, [provider]: !prev[provider] }));
	};

	const selectedProviders = CONFERENCE_PROVIDERS.filter(p => selected[p]);

	const lastMessage = progressLog[progressLog.length - 1] || '';

	const runComparison = async () => {
		if (!roomName || !selectedProviders.length || running) return;
		stopPoll();
		setRunning(true);
		setLogOpen(true);
		setResults([]);
		const now = Date.now();
		setStartedAt(now);
		setStepStartedAt(now);
		setUpdatedAt(now);
		lastLineRef.current = 'Starting…';
		setProgressLog(['Starting…']);
		try {
			// Fire-and-forget: the run continues in the background. `started:false` just means one was
			// already in flight — either way we poll the same room until it reaches a terminal phase.
			await ConferenceRest.compare(roomName, [...selectedProviders]);
			if (!aliveRef.current) return; // unmounted mid-request — don't start an orphan poll
			pollRef.current = window.setInterval(async () => {
				try {
					const res = await ConferenceRest.compareProgress(roomName);
					const progress = res?.progress;
					if (!progress) {
						// Finished + TTL expired, or nothing running — pick up whatever was saved.
						stopPoll();
						pushLog('Done');
						setRunning(false);
						await loadGroups(true);
						return;
					}
					setUpdatedAt(Date.now());
					setResults(progress.results || []);
					pushLog(compareProgressMessage(progress));
					if (progress.phase === 'DONE') {
						stopPoll();
						setRunning(false);
						await loadGroups(true);
					} else if (progress.phase === 'FAILED') {
						stopPoll();
						setRunning(false);
					}
				} catch (e) {
					console.error(e);
				}
			}, 2000);
		} catch (e) {
			console.error(e);
			stopPoll();
			setRunning(false);
			pushLog('Failed');
		}
	};

	// Delete one provider's set (persistent on the backend); confirm, then reload the columns.
	const deleteColumn = async (provider: TCaptionProvider) => {
		if (running || deleting) return;
		if (!window.confirm(`Delete the ${providerLabel(provider)} caption set? This can't be undone.`)) return;
		setDeleting(true);
		try {
			await AudioRest.deleteCaptions(audioId, provider);
			setResults(prev => prev.filter(r => r.provider !== provider));
			await loadGroups();
		} catch (e) {
			console.error(e);
		} finally {
			setDeleting(false);
		}
	};

	const resultByProvider = React.useMemo(
		() => Object.fromEntries(results.map(r => [r.provider, r])),
		[results]
	);

	const rows = React.useMemo(() => buildAlignedRows(groups), [groups]);

	// Highlight the row matching the player's current time. Poll the shared player (the panel is
	// outside the Redux captions pipeline) and recompute the active row only when its index changes.
	const [activeRow, setActiveRow] = React.useState<number>(-1);
	const [hoveredRow, setHoveredRow] = React.useState<number>(-1);
	const activeRowRef = React.useRef<HTMLDivElement | null>(null);

	React.useEffect(() => {
		if (!rows.length) return;
		const player = AudioPlayerManager.getInstance();
		const id = setInterval(() => {
			const t = player.getCurrentTime();
			let idx = -1;
			for (let i = 0; i < rows.length; i++) {
				if (rows[i].time <= t) idx = i; else break;
			}
			setActiveRow(prev => (prev === idx ? prev : idx));
		}, 250);
		return () => clearInterval(id);
	}, [rows]);

	React.useEffect(() => {
		activeRowRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
	}, [activeRow]);

	const cellBorder = `1px solid ${theme.palette.grey[100]}`;
	const activeRowBg = alpha(theme.palette.primary.main, 0.12);
	const hoverRowBg = theme.palette.grey[50];

	return (
		<Stack
			sx={{
				width: '100%',
				height: '100%',
				overflow: 'hidden',
			}}
		>
			{/* Control row: provider checkboxes + run */}
			<Stack
				flexDirection={'row'}
				alignItems={'center'}
				gap={2}
				sx={{
					p: theme.spacing(1),
					borderBottom: cellBorder,
					flexWrap: 'wrap',
				}}
			>
				{CONFERENCE_PROVIDERS.map(provider => (
					<FormControlLabel
						key={provider}
						control={
							<Checkbox
								size={'small'}
								checked={!!selected[provider]}
								onChange={() => toggleProvider(provider)}
								disabled={running}
							/>
						}
						label={provider}
					/>
				))}
				<Button
					variant={'contained'}
					onClick={runComparison}
					disabled={running || !selectedProviders.length || !roomName}
				>
					{running ? 'Running…' : 'Run comparison'}
				</Button>
				{running &&
					<Stack
						flexDirection={'row'}
						alignItems={'center'}
						gap={1}
						onClick={() => setLogOpen(true)}
						sx={{ cursor: 'pointer' }}
					>
						<CircularProgress size={theme.spacing(2)} />
						<Box sx={{ fontSize: theme.typography.subtitle1.fontSize, color: theme.palette.grey[400] }}>
							{lastMessage || 'Re-transcribing — this can take a few minutes...'}
						</Box>
					</Stack>
				}
			</Stack>

			{/* Aligned comparison grid */}
			<Box sx={{ flex: '1 1 auto', overflow: 'auto' }}>
				{loading && !groups.length
					? <Box sx={{ p: theme.spacing(2), color: theme.palette.grey[400] }}>Loading…</Box>
					: !groups.length
						? <Box sx={{ p: theme.spacing(2), color: theme.palette.grey[400] }}>No caption sets yet. Run a comparison.</Box>
						: <Box
							onMouseLeave={() => setHoveredRow(-1)}
							sx={{
								display: 'grid',
								gridTemplateColumns: `repeat(${groups.length}, minmax(0, 1fr))`,
								alignItems: 'start',
							}}
						>
							{/* Header row */}
							{groups.map((g, gi) => {
								const r = g.provider != null ? resultByProvider[g.provider] : undefined;
								// Only real (non-legacy) provider sets can be picked as the conference provider.
								const isConfProvider = g.provider != null && g.provider === confProvider;
								const canSelectProvider = g.provider != null && !isConfProvider && !running && !deleting;
								return (
									<Box
										key={`head-${gi}`}
										onMouseEnter={() => setHoveredRow(-1)}
										sx={{
											position: 'sticky',
											top: 0,
											zIndex: 1,
											background: theme.palette.common.white,
											borderBottom: cellBorder,
											borderLeft: gi ? cellBorder : 'none',
											p: theme.spacing(1),
											fontWeight: 600, '&:hover .delete-col': { opacity: 1 },
										}}
									>
										<Stack flexDirection={'row'} alignItems={'center'} justifyContent={'space-between'} gap={0.5}>
											<Tooltip title={isConfProvider
													? 'Conference current provider'
													: canSelectProvider
														? 'Use this provider for the conference'
														: ''
											}>
												<Stack
													flexDirection={'row'}
													alignItems={'center'}
													gap={0.5}
													onClick={canSelectProvider ? () => onProviderChange(g.provider as string, g.captions) : undefined}
													sx={{
														cursor: canSelectProvider ? 'pointer' : 'default',
														color: isConfProvider ? theme.palette.primary.main : 'inherit',
														'&:hover': canSelectProvider ? { color: theme.palette.primary.main } : undefined,
													}}
												>
													<Box>{providerLabel(g.provider)}</Box>
													{isConfProvider &&
														<CheckIcon sx={{ width: theme.spacing(1.5), height: theme.spacing(1.5), color: theme.palette.primary.main }} />
													}
												</Stack>
											</Tooltip>
											<Tooltip title={'Delete this caption set'}>
												<IconButton
													className={'delete-col'}
													size={'small'}
													disabled={running || deleting}
													onClick={() => deleteColumn(g.provider)}
													sx={{
														opacity: 0,
														transition: 'opacity .15s ease',

														'&:hover': {
															color: theme.palette.error.main,
														},
													}}
												>
													<TrashIcon sx={{ width: theme.spacing(1.75), height: theme.spacing(1.75) }} />
												</IconButton>
											</Tooltip>
										</Stack>
										<Box
											sx={{
												fontWeight: 400,
												fontSize: theme.typography.subtitle1.fontSize,
												color: r && r.ok === false ? theme.palette.error.main : theme.palette.grey[400],
											}}
										>
											{g.captions.length} captions
											{r && r.ok === false && r.error ? ` · ${r.error}` : ''}
										</Box>
									</Box>
								);
							})}

							{/* Caption rows */}
							{rows.map((row, ri) =>
								row.cells.map((caption, gi) => (
									<Box
										key={`r${ri}-c${gi}`}
										ref={ri === activeRow && gi === 0 ? activeRowRef : undefined}
										onMouseEnter={() => setHoveredRow(ri)}
										onDoubleClick={() => AudioPlayerManager.getInstance().play(caption ? caption.startTime : row.time)}
										sx={{
											borderBottom: cellBorder,
											borderLeft: gi ? cellBorder : 'none',
											p: theme.spacing(1),
											fontSize: theme.typography.subtitle2.fontSize,
											background: ri === activeRow ? activeRowBg : ri === hoveredRow ? hoverRowBg : 'transparent',
											transition: 'background .15s ease',
											cursor: 'pointer',
											userSelect: 'none',
										}}
									>
										{caption &&
											<>
												<Box
													sx={{
														fontSize: theme.typography.subtitle1.fontSize,
														color: theme.palette.grey[400],
														fontVariantNumeric: 'tabular-nums',
													}}
												>
													{formatTime(caption.startTime)}–{formatTime(caption.endTime)}
													{caption.userName ? ` · ${caption.userName}` : ''}
												</Box>
												<Box>{caption.captionText || caption.words.map(w => w.text).join('')}</Box>
											</>
										}
									</Box>
								))
							)}
						</Box>
				}
			</Box>

			{progressLog.length > 0 &&
				<CallRecordingProgressModalHOC
					open={logOpen}
					onClose={() => setLogOpen(false)}
					title={'Provider comparison'}
					progressLog={progressLog}
					isProcessing={running}
					startedAt={startedAt}
					stepStartedAt={stepStartedAt}
					updatedAt={updatedAt}
				/>
			}
		</Stack>
	);
}
